/**
 * Encolhe a foto no navegador antes de subir.
 *
 * Não existe compressão nenhuma no caminho: o backend valida o mimetype,
 * renomeia e copia o arquivo como veio (não há `sharp`/`jimp` nas dependências),
 * o multer aceita 100 MB e o nginx serve com cache de 30 dias. Resultado prático
 * hoje em produção: um `lp_*.jpeg` de 10,5 MB servido cru em `/uploads/landing`.
 *
 * O celular de quem monta a página tira foto de 4032px. Como fundo de lâmina ela
 * é desenhada em `cover` numa faixa que nunca passa de ~2400px úteis — então os
 * megabytes extras não aparecem na tela, só na conta de quem clicou no anúncio
 * pelo 4G. É a mesma preocupação que `scripts/check-bundle-landing.cjs` defende
 * para o JS, e que ele NÃO pega, porque imagem não é bundle.
 *
 * Mora em `src/lib/` e não em `src/landing/`: a landing pública alcança tudo que
 * está lá, e isto é ferramenta de painel autenticado. Quem chama são os dois
 * pontos de upload de IMAGEM (`CampoImagem` e `LandingCanvas`), nunca o desenho.
 *
 * **Nunca falha.** Qualquer tropeço devolve o arquivo original — um upload que
 * não acontece é pior do que um arquivo grande.
 */

/**
 * Formatos que o canvas sabe re-encodar sem perder o que importa, e o tipo de
 * saída de cada um.
 *
 * PNG sai como PNG de propósito. Re-encodar para JPEG economizaria bem mais e
 * mataria a transparência — e o `texturaImg` do padrão de fundo é justamente um
 * PNG transparente (o próprio editor pede "um PNG com fundo transparente"). O
 * ladrilho viraria um retângulo preto repetido sobre a seção inteira.
 *
 * O que não está aqui passa intacto:
 *   · SVG  — vetor, não há o que encolher, e virar raster é perda pura
 *   · GIF  — o canvas devolveria só o primeiro quadro, matando a animação
 *   · MP4  — nem é imagem; os campos aceitam vídeo
 *   · HEIC — o `createImageBitmap` da maioria dos navegadores nem decodifica
 */
const SAIDA: Record<string, { tipo: string; ext: string }> = {
  'image/jpeg': { tipo: 'image/jpeg', ext: '.jpg' },
  'image/png':  { tipo: 'image/png',  ext: '.png' },
  'image/webp': { tipo: 'image/webp', ext: '.webp' },
}

/** Largura acima da qual não há ganho visível em nenhuma tela que a landing serve. */
export const LARGURA_MAX = 2400

/** Qualidade do re-encode com perda. Acima disto o arquivo cresce sem diferença. */
const QUALIDADE = 0.86

export async function encolherImagem(f: File, larguraMax = LARGURA_MAX): Promise<File> {
  const saida = SAIDA[f.type]
  if (!saida) return f

  let bmp: ImageBitmap | undefined
  try {
    // `imageOrientation` é obrigatório: sem ele, foto de celular em retrato sai
    // DEITADA. O navegador respeita o EXIF ao mostrar uma <img>, mas
    // `drawImage` desenha os pixels crus — o giro se perde no canvas e o
    // arquivo salvo fica errado para sempre. É o bug clássico de foto de iPhone,
    // e ele só aparece depois do deploy.
    bmp = await createImageBitmap(f, { imageOrientation: 'from-image' })
    if (bmp.width <= larguraMax) return f

    const w = larguraMax
    const h = Math.round((bmp.height * larguraMax) / bmp.width)
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return f
    ctx.drawImage(bmp, 0, 0, w, h)

    const blob = await new Promise<Blob | null>(ok => canvas.toBlob(ok, saida.tipo, QUALIDADE))
    // Blob maior que o original acontece com JPEG já bem comprimido: nesse caso
    // encolher piorou, e o certo é não fazer nada.
    if (!blob || blob.size >= f.size) return f

    // A extensão TEM de acompanhar o tipo: o backend monta o nome final com
    // `path.extname(originalname)` e valida o mimetype separado. Manter um nome
    // `.png` com bytes WebP faria o nginx servir `image/png` para outra coisa.
    const nome = f.name.replace(/\.[^.]+$/, '') + saida.ext
    return new File([blob], nome, { type: saida.tipo, lastModified: f.lastModified })
  } catch {
    return f
  } finally {
    bmp?.close()
  }
}
